import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Box, Button, Spinner, TextField, Typography } from '@glplus/glpweb-dsl';
import { Panel } from '../components/Panel';
import { PickButton, PRIMARY_SX } from '../components/PickButton';
import { runnerApi, RunnerApiError, type InsurancePolicy, type MyInsurance } from '../lib/runnerApiClient';
import { formatCardDate, insuranceFormError, localTodayYmd } from '../lib/insurance';
import { ORANGE, ORANGE_LINK_STYLE } from '../styles/colors';

const NAVY = '#343F5B';
const GREEN = '#2E7D32';

type Flash = { kind: 'success' | 'error'; msg: string } | null;

/**
 * Car insurance card (Rod, 2026-10-01). Emails link here as `<portal>/insurance`,
 * so the path is fixed. Onboarding uses it at `insurance_review`; live Runners
 * come back to it for every renewal.
 */
export default function Insurance({ onChanged }: { onChanged: () => Promise<void> }) {
  const { t, i18n } = useTranslation();
  const [state, setState] = useState<MyInsurance | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [flash, setFlash] = useState<Flash>(null);
  const [busy, setBusy] = useState(false);
  const [insurer, setInsurer] = useState('');
  const [policyNumber, setPolicyNumber] = useState('');
  const [expiresOn, setExpiresOn] = useState('');
  const [file, setFile] = useState<File | null>(null);

  const load = useCallback(async () => {
    try {
      setState(await runnerApi.myInsurance());
      setError(null);
    } catch (e) {
      setError(e instanceof RunnerApiError ? e.message : t('insurance.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    void load();
  }, [load]);

  async function submit() {
    const problem = insuranceFormError({ insurer, policyNumber, expiresOn, file }, localTodayYmd());
    if (problem || !file) return setFlash({ kind: 'error', msg: t(`insurance.${problem ?? 'missingFields'}`) });
    setBusy(true);
    setFlash(null);
    try {
      await runnerApi.submitInsurance({ insurer: insurer.trim(), policyNumber: policyNumber.trim(), expiresOn }, file);
      setInsurer('');
      setPolicyNumber('');
      setExpiresOn('');
      setFile(null);
      setFlash({ kind: 'success', msg: t('insurance.submitted') });
      await load();
      await onChanged();
    } catch (e) {
      setFlash({ kind: 'error', msg: e instanceof RunnerApiError ? e.message : t('insurance.genericError') });
    } finally {
      setBusy(false);
    }
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', pt: 10 }}>
        <Spinner aria-label={t('insurance.loading')} />
      </Box>
    );
  }
  if (error && !state) return <Alert severity="error" data-testid="insurance-load-error">{error}</Alert>;

  if (state && !state.ready) {
    return (
      <Box data-testid="page-insurance">
        <Header />
        <Panel title={t('insurance.notOpenTitle')} accent={NAVY} sx={{ maxWidth: 640 }} dataTestId="insurance-not-open">
          <Typography sx={{ color: '#4b5563', fontSize: '14px' }}>{t('insurance.notOpenBody')}</Typography>
        </Panel>
      </Box>
    );
  }

  const current = state?.current ?? null;
  const pending = state?.pending ?? null;
  const sentBack = state?.lastSentBack ?? null;
  const lang = i18n.language;

  return (
    <Box data-testid="page-insurance">
      <Header />
      {flash && (
        <Box sx={{ mb: 2 }}>
          <Alert severity={flash.kind} data-testid={`insurance-${flash.kind}`}>{flash.msg}</Alert>
        </Box>
      )}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2.5, alignItems: 'start' }}>
        <Box sx={{ display: 'grid', gap: 2.5 }}>
          <Panel title={t('insurance.currentTitle')} accent={current && state?.currentValid ? GREEN : NAVY} dataTestId="insurance-current">
            {current ? (
              <PolicyRows
                policy={current}
                lang={lang}
                pill={state?.currentValid ? { label: t('insurance.pillApproved'), bg: '#e7f4e8', fg: GREEN } : { label: t('insurance.pillExpired'), bg: '#fde8e8', fg: '#b42318' }}
              />
            ) : (
              <Typography sx={{ color: '#5f6b7a', fontSize: '14px' }}>{t('insurance.noneBody')}</Typography>
            )}
          </Panel>
          {pending && (
            <Panel title={t('insurance.pendingTitle')} accent={ORANGE} dataTestId="insurance-pending">
              <Typography sx={{ color: '#5f6b7a', fontSize: '13px', mb: 1.5 }}>{t('insurance.pendingBody')}</Typography>
              <PolicyRows policy={pending} lang={lang} pill={{ label: t('insurance.pillPending'), bg: '#fdf1e3', fg: '#9a5b12' }} />
            </Panel>
          )}
          {sentBack && !pending && (
            <Box data-testid="insurance-sent-back">
              {/* Shown verbatim — the reviewer wrote it for this Runner to read. */}
              <Alert severity="warning" title={t('insurance.sentBackTitle')}>{sentBack.rejectReason ?? t('insurance.sentBackNoReason')}</Alert>
            </Box>
          )}
        </Box>

        {!pending && (
          <Panel title={current ? t('insurance.renewTitle') : t('insurance.uploadTitle')} accent={ORANGE} dataTestId="insurance-upload">
            <Typography sx={{ color: '#5f6b7a', fontSize: '13px', mb: 2 }}>{t('insurance.uploadIntro')}</Typography>
            <Box sx={{ display: 'grid', gap: 2 }}>
              <TextField label={t('insurance.insurer')} ariaLabel={t('insurance.insurer')} dataTestId="insurance-insurer" size="small" fullWidth value={insurer} disabled={busy} placeholder={t('insurance.insurerPlaceholder')} onChange={(e) => setInsurer(e.target.value)} />
              <TextField label={t('insurance.policyNumber')} ariaLabel={t('insurance.policyNumber')} dataTestId="insurance-policy-number" size="small" fullWidth value={policyNumber} disabled={busy} onChange={(e) => setPolicyNumber(e.target.value)} />
              <TextField label={t('insurance.expiresOn')} ariaLabel={t('insurance.expiresOn')} dataTestId="insurance-expires-on" type="date" size="small" fullWidth value={expiresOn} disabled={busy} onChange={(e) => setExpiresOn(e.target.value)} />
            </Box>
            <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap', mt: 2 }}>
              <PickButton label={file ? t('insurance.replaceCard') : t('insurance.chooseCard')} dataTestId="insurance-card-pick" disabled={busy} onFile={setFile} />
              {file && <Typography data-testid="insurance-card-chosen" sx={{ color: '#4b5563', fontSize: '13px', wordBreak: 'break-word' }}>{t('insurance.cardChosen', { name: file.name })}</Typography>}
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
              <Button ariaLabel={t('insurance.submit')} dataTestId="insurance-submit" buttonText={busy ? t('insurance.submitting') : t('insurance.submit')} size="small" variant="contained" disabled={busy} sx={PRIMARY_SX} onClick={() => void submit()} />
            </Box>
          </Panel>
        )}
      </Box>
    </Box>
  );
}

function Header() {
  const { t } = useTranslation();
  return (
    <Box sx={{ mb: 2.5 }}>
      <Typography variant="h4" component="h1" sx={{ fontWeight: 800, color: '#1f2937', fontSize: { xs: '24px', md: '30px' } }}>{t('insurance.title')}</Typography>
      <Typography sx={{ color: '#5f6b7a', fontSize: '14px', mt: 0.5 }}>{t('insurance.subtitle')}</Typography>
    </Box>
  );
}

function PolicyRows({ policy, lang, pill }: { policy: InsurancePolicy; lang: string; pill: { label: string; bg: string; fg: string } }) {
  const { t } = useTranslation();
  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
        <Box sx={{ backgroundColor: pill.bg, color: pill.fg, borderRadius: 999, px: 1.25, py: 0.35, fontSize: '11px', fontWeight: 800 }}>{pill.label}</Box>
      </Box>
      <Row label={t('insurance.insurer')} value={policy.insurer} />
      <Row label={t('insurance.policyNumber')} value={policy.policyNumber} />
      <Row
        label={t('insurance.expiresOn')}
        value={policy.daysLeft >= 0 ? `${formatCardDate(policy.expiresOn, lang)} · ${t('insurance.daysLeft', { count: policy.daysLeft })}` : formatCardDate(policy.expiresOn, lang)}
      />
      {policy.cardViewUrl && (
        <Box component="a" href={policy.cardViewUrl} target="_blank" rel="noopener noreferrer" data-testid={`insurance-view-card-${policy.status}`} sx={{ display: 'inline-block', mt: 1.5, ...ORANGE_LINK_STYLE }}>
          {t('insurance.viewCard')}
        </Box>
      )}
    </Box>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: '130px 1fr', gap: 1, py: 0.75, borderBottom: '1px solid #f1f5f9' }}>
      <Typography sx={{ color: '#5f6b7a', fontSize: '13px' }}>{label}</Typography>
      <Typography sx={{ color: '#1f2937', fontSize: '14px', fontWeight: 600, wordBreak: 'break-word' }}>{value}</Typography>
    </Box>
  );
}
